import { Component, OnInit, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatInputModule } from '@angular/material/input';
import { MatTabsModule } from '@angular/material/tabs';
import { MatDialog } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { MatMenuModule } from '@angular/material/menu';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatSelectModule } from '@angular/material/select';
import { ActivatedRoute, Router } from '@angular/router';
import { Subject, debounceTime, distinctUntilChanged } from 'rxjs';
import { ToastrService } from 'ngx-toastr';
import { ConfirmDialog } from '../../shared/components/confirm-dialog/confirm-dialog';
import { PettyCashService } from '../../features/petty-cash/services/petty-cash.service';
import { ModuleIds } from '../../shared/permission-constants/permission-const';
import { PermissionService } from '../../shared/services/permission.service';

@Component({
  selector: 'app-petty-cash-details',
  standalone: true,
  imports: [
    CommonModule,
    MatInputModule,
    MatTabsModule,
    MatMenuModule,
    MatIconModule,
    MatSelectModule,
    ReactiveFormsModule,
    FormsModule,
  ],
  templateUrl: './petty-cash-details.component.html',
  styleUrl: './petty-cash-details.component.scss',
})
export class PettyCashDetails implements OnInit {
  selectedTabIndex = 0;
  pettyCashId: any;
  employeeId: any;
  pettyCashData: any;
  currentData: any;
  allPettyCash: any[] = [];
  public loading: boolean = true;
  private searchSubject = new Subject<string>();
  canCreatePettyCash$ = this.permissionService.hasPermission(
    ModuleIds.PettyCash,
    'Create'
  );
canEditPettyCash$ = this.permissionService.hasPermission(
    ModuleIds.PettyCash,
    'Edit'
  );
  canApproveRejectPettyCash$ = this.permissionService.hasPermission(
    ModuleIds.PettyCash,
    'Approve/Reject'
  ); 
  constructor(
    private router: Router,
    private route: ActivatedRoute,
    private toastr: ToastrService,
    private pettyCashService: PettyCashService,
    private permissionService: PermissionService
  ) {}

  ngOnInit(): void {
    this.route.queryParamMap.subscribe((queryParams) => {
      this.pettyCashId = queryParams.get('pettyCashId');
      this.employeeId = queryParams.get('employeeId');
      this.fetchAllPettyCash();
    });

    this.searchSubject
      .pipe(debounceTime(600), distinctUntilChanged())
      .subscribe((searchTerm) => {
        this.fetchAllPettyCash(searchTerm);
      });
  }

  navigateTo(route: string) {
    this.router.navigate([route], {
      queryParams: { employeeId: this.employeeId, pettyCashId: this.pettyCashId },
    });
  }

  onEditActivity(route: string, activityId: any){
    this.router.navigate([route], {
      queryParams: { pettyCashId: this.pettyCashId, activityId, employeeId: this.employeeId},
    });
  }

  onSearch(event: Event) {
    const input = event.target as HTMLInputElement;
    const searchTerm = input.value;
    this.searchSubject.next(searchTerm);
  }

  fetchAllPettyCash(search: string = '') {
    this.loading = true;
    this.pettyCashService.fetchPettyCashAccounts(search, true).subscribe({
      next: (pettyCash) => {
        this.allPettyCash = pettyCash || [];

        if (this.pettyCashId) {
          const targetId = +this.pettyCashId;
          const index = this.allPettyCash.findIndex(
            (c: any) => c.id === targetId
          );
          this.selectedTabIndex = index !== -1 ? index : 0;
          this.pettyCashData = this.allPettyCash[this.selectedTabIndex] || {};
          this.getPettyCashActivity(targetId);
          this.currentData = this.allPettyCash[this.selectedTabIndex] || {};          
        } else {
          this.selectedTabIndex = 0;
          this.pettyCashData = this.allPettyCash[0] || {};
          this.currentData = this.allPettyCash[0] || {};
        }
      },
      error: (err) => {
        console.error('Error fetching invoices:', err);
      },
    });
  }

  getPettyCashActivity(id: any) {
    this.loading = true;
    this.pettyCashService.fetchPettyCashActivities(Number(id)).subscribe({
      next: (data) => {
        this.pettyCashData = data;
        this.loading = false
      },
      error: (err) => {
        console.log(err);
        this.loading = false
      },
    });
  }

  selectTab(index: number) {
    this.selectedTabIndex = index;
    this.pettyCashData = this.allPettyCash[index];
    if (this.pettyCashData) {
      this.router.navigate(['petty-cash/petty-cash-details'], {
        queryParams: { pettyCashId: this.pettyCashData.id, employeeId: this.pettyCashData?.employeeId },
      });
    }
  }

  get totalIn() {
    const data = Array.isArray(this.pettyCashData) ? this.pettyCashData : [];
    return data
      .filter((item: any) => item?.type === 'IN' && item?.activityStatus === 'Approved')
      .reduce((sum: number, item: any) => sum + Number(item?.amount || 0), 0);
  }
  
  get totalOut() {
    const data = Array.isArray(this.pettyCashData) ? this.pettyCashData : [];
    return data
      .filter((item: any) => item?.type === 'OUT' && item?.activityStatus === 'Approved')
      .reduce((sum: number, item: any) => sum + Number(item?.amount || 0), 0);
  }
  


  handleApproval( pettyCashId: any, isApproved: boolean): void {
    const dialogRef = this.dialog.open(ConfirmDialog, {
      data: {
        title: isApproved ? 'Confirm Approval' : 'Confirm Rejection',
        message: isApproved
          ? 'Are you sure you want to approve this petty cash?'
          : 'Are you sure you want to reject this petty cash?',
        confirmText: 'Confirm',
      },
    });
    dialogRef.afterClosed().subscribe((result) => {
      if (result === true) {
        this.pettyCashService
          .approveOrRejectPettyCashActivity(Number(pettyCashId), isApproved)
          .subscribe({
            next: (res) => {
              console.log(res);
              this.toastr.success('Petty cash status updated.');
              this.fetchAllPettyCash();
              this.getPettyCashActivity(this.pettyCashId);
            },
            error: (err) => {
              this.toastr.error(err?.message || 'Error updating petty cash')
              console.error('Error updating petty cash : ', err);
            },
          });
      }
    });
  }

  readonly dialog = inject(MatDialog);

}
